/**
 * Hook para polling de configuración de marquesina cada 30 segundos
 * @returns {{ marquee: string|null, marqueeDesp: string|null, loading: boolean }}
 */

import { useState, useEffect, useRef } from 'react';
import EntregasService from '../api/entregas.api';
import { POLLING_INTERVAL, CONFIG_KEYS } from '../constants';

export const useMarquesina = () => {
    const [marquee, setMarquee] = useState(null);
    const [marqueeDesp, setMarqueeDesp] = useState(null);
    const [loading, setLoading] = useState(true);
    const isFirstLoad = useRef(true);

    useEffect(() => {
        /**
         * Función para obtener la configuración de marquesina desde la API
         * Solo muestra loading en la primera carga, no en los refreshes automáticos
         */
        const fetchMarquesina = async () => {
            try {
                // Solo mostrar loading en la primera carga
                if (isFirstLoad.current) {
                    setLoading(true);
                }

                // Ejecutar ambas llamadas en paralelo
                const [configMarquee, configMarqueeDesp] = await Promise.all([
                    EntregasService.getConfigMarquesina(CONFIG_KEYS.MARQUESINA).catch(() => null),
                    EntregasService.getConfigMarquesina(CONFIG_KEYS.MARQUESINA_DESP).catch(() => null)
                ]);

                // Extraer el valor de cada respuesta
                setMarquee(configMarquee?.value ?? null);
                setMarqueeDesp(configMarqueeDesp?.value ?? null);

                // Marcar que ya no es la primera carga
                if (isFirstLoad.current) {
                    isFirstLoad.current = false;
                }
            } catch (err) {
                console.error('Error al cargar configuración de marquesina:', err);
                // Error silencioso: marquesina es opcional, no romper la vista
                setMarquee(null);
                setMarqueeDesp(null);
            } finally {
                // Solo quitar loading si estaba activo
                if (loading) {
                    setLoading(false);
                }
            }
        };

        // Fetch inicial al montar
        fetchMarquesina();

        // Configurar polling cada 30 segundos (actualiza en background)
        const intervalMarquesina = setInterval(fetchMarquesina, POLLING_INTERVAL);

        // Cleanup: limpiar interval al desmontar
        return () => {
            clearInterval(intervalMarquesina);
        };
    }, []); // Sin dependencias: la marquesina no depende del contexto

    return {
        marquee,
        marqueeDesp,
        loading
    };
};
